// ================================================================
// WhatsApp API — اتصال از طریق Evolution API (org-scoped, multi-instance)
// همه‌ی توابع instanceId می‌گیرند. الگوی apiRequest() مثل core/email.js
// ================================================================
// getToken() bleibt hier direkt importiert: getWhatsAppMediaBlob() liefert eine
// binäre Antwort (Blob), nicht JSON — kann nicht über apiRequest() laufen.
import { getToken } from './auth.js';
import { apiRequest, readErrorMessage } from './apiClient.js';

const base = '/api/whatsapp';
const inst = (id) => base + '/instances/' + encodeURIComponent(id);

// ── مدیریت شماره‌ها (instance ها) ──

// «Hat diese Nummer WhatsApp?» — für die Empfänger-Felder der Send-Dialoge.
// Antwort { known, exists, jid }: known=false heisst NICHT PRÜFBAR (keine
// verbundene Instanz) — nie als «kein WhatsApp» anzeigen.
export function checkWhatsAppNumber(number) {
  return apiRequest(base + '/check-number', {
    method: 'POST',
    body: JSON.stringify({ number }),
  });
}

// لیست همه‌ی شماره‌های سازمان
export function listWhatsAppInstances() {
  return apiRequest(base + '/instances');
}

// ساخت شماره‌ی جدید (label اختیاری)
export function createWhatsAppInstance(label) {
  return apiRequest(base + '/instances', {
    method: 'POST',
    body: JSON.stringify({ label: label || null }),
  });
}

// حذف شماره
export function removeWhatsAppInstance(instanceId) {
  return apiRequest(inst(instanceId), { method: 'DELETE' });
}

/** WhatsApp-Daten dieser Nummer löschen (Chats/Nachrichten/Medien) — die
 *  Nummer bleibt verbunden. Gegenstück zu deleteConnectionData (Gmail). */
export function deleteWhatsAppData(instanceId) {
  return apiRequest(inst(instanceId) + '/data', { method: 'DELETE' });
}

// تغییر نام شماره
export function updateWhatsAppInstanceLabel(instanceId, label) {
  return apiRequest(inst(instanceId) + '/label', {
    method: 'PATCH',
    body: JSON.stringify({ label }),
  });
}

// ── اتصال / وضعیت / قطع ──

// اتصال: QR (base64 data-URL) برمی‌گرداند؛ با number علاوه بر QR یک
// Pairing Code هم می‌آید (اتصال روی خودِ گوشی، بدون اسکن)
export function connectWhatsApp(instanceId, number) {
  return apiRequest(inst(instanceId) + '/connect', {
    method: 'POST',
    body: JSON.stringify(number ? { number } : {}),
  });
}

// وضعیت اتصال
export function getWhatsAppStatus(instanceId) {
  return apiRequest(inst(instanceId) + '/status');
}

// قطع اتصال
export function disconnectWhatsApp(instanceId) {
  return apiRequest(inst(instanceId) + '/disconnect', { method: 'POST' });
}

// ── چت‌ها و پیام‌ها ──

// لیست چت‌ها
export function getWhatsAppChats(instanceId) {
  return apiRequest(inst(instanceId) + '/chats');
}

// ترجمه‌ی on-demand یک پیام قدیمی («دکمه‌ی ترجمه» — خارج از پنجره‌ی خودکار)
export function translateWhatsAppMessage(messageDbId, lang) {
  return apiRequest(base + '/messages/' + encodeURIComponent(messageDbId) + '/translate', {
    method: 'POST',
    body: JSON.stringify({ lang }),
  });
}

// پیام‌های یک چت خاص (uiLang: دریافتی‌ها به این زبان ترجمه/کش می‌شوند)
export function getWhatsAppMessages(instanceId, remoteJid, uiLang) {
  const q = uiLang ? '?lang=' + encodeURIComponent(uiLang) : '';
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/messages' + q);
}

// ارسال پیام به یک چت (quotedMessageId = ریپلای؛ lang = ترجمه قبل از ارسال)
export function sendWhatsAppMessage(instanceId, remoteJid, text, quotedMessageId, lang) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/send', {
    method: 'POST',
    body: JSON.stringify({
      text,
      ...(quotedMessageId ? { quotedMessageId } : {}),
      ...(lang ? { lang } : {}),
    }),
  });
}

// زبانِ ارسال برای یک چت («به خاطر بسپار» + منوی +)
export function setWhatsAppChatLanguage(instanceId, remoteJid, language, remember) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/send-language', {
    method: 'PATCH',
    body: JSON.stringify({ language, remember }),
  });
}

// ویرایش پیام متنی خودمان
export function editWhatsAppMessage(instanceId, remoteJid, messageId, text) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/messages/' + encodeURIComponent(messageId), {
    method: 'PATCH',
    body: JSON.stringify({ text }),
  });
}

// حذف پیام — scope: 'me' (فقط برای من) | 'all' (برای همه، فقط پیام خودمان)
export function deleteWhatsAppMessage(instanceId, remoteJid, messageId, scope) {
  return apiRequest(
    inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/messages/' + encodeURIComponent(messageId) + '?scope=' + (scope === 'all' ? 'all' : 'me'),
    { method: 'DELETE' },
  );
}

// حذف یک چت (آینه‌ی محلی — مثل «Chat löschen»)
export function deleteWhatsAppChat(instanceId, remoteJid) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid), { method: 'DELETE' });
}

// ارسال عکس/ویدیو/فایل از کامپوزر چت (base64 بدون data: prefix)
export function sendWhatsAppChatMedia(instanceId, remoteJid, { base64, fileName, mimetype, caption }) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/send-media', {
    method: 'POST',
    body: JSON.stringify({ base64, fileName, mimetype, caption }),
  });
}

// ارسال ویسِ ضبط‌شده از میکروفن (base64 بدون data: prefix)
export function sendWhatsAppRecordedAudio(instanceId, remoteJid, base64, mimetype) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/send-audio', {
    method: 'POST',
    body: JSON.stringify({ base64, mimetype }),
  });
}

// ویسِ ضبط‌شده → متن (شیت «نحوه ارسال ویس»)
export function transcribeWhatsAppRecording(instanceId, remoteJid, base64, mimetype) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/transcribe', {
    method: 'POST',
    body: JSON.stringify({ base64, mimetype }),
  });
}

// نحوهٔ ارسال ویس برای این مخاطب («به خاطر بسپار»)
export function setWhatsAppChatVoiceMode(instanceId, remoteJid, mode, remember) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/voice-mode', {
    method: 'PATCH',
    body: JSON.stringify({ mode, remember }),
  });
}

// ارسالِ پیامِ صوتی (متن → صوت) از کادرِ تایپ
export function sendWhatsAppVoice(instanceId, remoteJid, text) {
  return apiRequest(inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/send-voice', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// گرفتن فایل media به‌صورت blob (با توکن) — برای ساخت objectURL در رندر
export async function getWhatsAppMediaBlob(instanceId, remoteJid, messageId) {
  const token = getToken();
  const res = await fetch(
    inst(instanceId) + '/chats/' + encodeURIComponent(remoteJid) + '/media/' + encodeURIComponent(messageId),
    { headers: { ...(token ? { Authorization: 'Bearer ' + token } : {}) } },
  );
  if (!res.ok) {
    const err = new Error('HTTP ' + res.status);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

// ── جوابِ پیشنهادیِ AI (درافت منتظرِ تأیید) ──

// خواندنِ درافتِ فعالِ یک چت
export function getChatDraft(chatId) {
  return apiRequest(base + '/chats/' + encodeURIComponent(chatId) + '/draft');
}

// تأیید و ارسالِ درافت (متنِ نهایی از مدیر)
export function sendDraft(draftId, text) {
  return apiRequest(base + '/drafts/' + encodeURIComponent(draftId) + '/send', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// تأیید و ارسالِ درافت به‌صورتِ صوتی (متن → صوت)
export function sendDraftVoice(draftId, text) {
  return apiRequest(base + '/drafts/' + encodeURIComponent(draftId) + '/send-voice', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

// رد کردنِ درافت
export function dismissDraft(draftId) {
  return apiRequest(base + '/drafts/' + encodeURIComponent(draftId) + '/dismiss', {
    method: 'POST',
  });
}

